import { useToast } from '@/components/ui/use-toast';
import { getGridSalesDraftAddress } from '@/features/sales-draft-header/components/form-shipping-address-field';
import { useAppState } from '@/lib/hooks/use-app-state';
import type { SalesDraftType } from '@/server/types/drafts-types';
import type { GridSalesDraftDefaultHeaderResponse } from '@/server/types/infer-trpc-types';
import { api } from '@/utils/api';
import { SrCustomerAddressType } from '@plato/data/starrocks/types';
import { useEffect, useMemo } from 'react';

interface Options {
    customerId: string | null | undefined;
    orderType: SalesDraftType;
}

export const useSalesDraftDefaults = ({ customerId, orderType }: Options) => {
    const { platoUser } = useAppState();
    const { toast } = useToast();

    const {
        isLoading: isLoadingQuery,
        data: gridDefaults,
        error,
        isError,
    } = api.platoUser.query.salesDraft.getSalesDraft.getDefaultHeader.useQuery(
        {
            customer_id: customerId || '',
            clerk_id: platoUser?.erpUserId || '',
            sales_order_type: orderType,
        },
        {
            enabled: !!customerId,
        },
    );

    const { data: addresses, isLoading: isLoadingPrimaryAddresses } =
        api.platoUser.getManyCustomerAddress.get.useQuery(
            {
                customerId: customerId || '',
            },
            {
                enabled: !!customerId,
            },
        );

    const isLoadingDefault = customerId ? isLoadingQuery || isLoadingPrimaryAddresses : false;

    const srPrimaryAddress = useMemo(() => {
        return addresses?.find((a) => a.type === SrCustomerAddressType.PRIMARY);
    }, [addresses]);

    const srBillingAddress = useMemo(() => {
        return addresses?.find((a) => a.type === SrCustomerAddressType.BILLING) ?? srPrimaryAddress;
    }, [addresses, srPrimaryAddress]);

    // Enhanced defaults with address fallback logic
    const defaults = useMemo<GridSalesDraftDefaultHeaderResponse | undefined>(() => {
        if (!gridDefaults) {
            return undefined;
        }

        if (gridDefaults.invoice_address && gridDefaults.shipping_address) {
            return gridDefaults;
        }

        if (isLoadingPrimaryAddresses) {
            return undefined;
        }

        if (!srPrimaryAddress || !srBillingAddress) {
            return gridDefaults;
        }

        return {
            ...gridDefaults,
            shipping_address: gridDefaults.invoice_address ?? getGridSalesDraftAddress(srBillingAddress),
            invoice_address: gridDefaults.shipping_address ?? getGridSalesDraftAddress(srPrimaryAddress),
        };
    }, [gridDefaults, srPrimaryAddress, srBillingAddress, isLoadingPrimaryAddresses]);

    useEffect(() => {
        if (isError) {
            toast({
                title: 'error',
                description: error.message,
                icon: 'warning',
            });
        }
    }, [isError, error]);

    return {
        isLoadingDefault,
        defaults,
    };
};
